// The player's Director of Football is built from the same system the editor
// uses to generate every club's DoF (groundworkdb/src/DofMigration.js): a
// BACKGROUND that biases seven attributes. Keep these weights in sync with the
// editor so the player DoF is a true peer of the AI DoFs.

export const DOF_ATTRS = [
  { key: 'scoutingNetwork',      label: 'Scouting Network' },
  { key: 'judgment',             label: 'Judgment' },
  { key: 'negotiation',          label: 'Negotiation' },
  { key: 'boardRelationship',    label: 'Board Relationship' },
  { key: 'managerRelationship',  label: 'Manager Relationship' },
  { key: 'transferBudgetControl', label: 'Budget Control' },
  { key: 'youthFocus',           label: 'Youth Focus' },
];

export const DOF_BACKGROUNDS = [
  {
    id: 'Former Scout',
    blurb: 'You came up watching players nobody else had seen. A sharp eye and a deep little black book.',
    weights: { scoutingNetwork: 1.4, judgment: 1.3, negotiation: 0.9, boardRelationship: 0.8, managerRelationship: 1.1, transferBudgetControl: 0.8, youthFocus: 1.2 },
  },
  {
    id: 'Former Manager',
    blurb: "You have sat in the dugout. Managers trust you, and you read a squad's problems at a glance.",
    weights: { scoutingNetwork: 0.9, judgment: 1.2, negotiation: 1.1, boardRelationship: 1.2, managerRelationship: 1.4, transferBudgetControl: 1.1, youthFocus: 1.0 },
  },
  {
    id: 'Club Executive',
    blurb: 'A boardroom operator. You win the room, control the budget and get deals signed off.',
    weights: { scoutingNetwork: 0.9, judgment: 0.9, negotiation: 1.4, boardRelationship: 1.5, managerRelationship: 0.9, transferBudgetControl: 1.4, youthFocus: 1.0 },
  },
  {
    id: 'Football Agent',
    blurb: 'You know every deal, every clause and every number. Few people out-negotiate you.',
    weights: { scoutingNetwork: 1.2, judgment: 1.1, negotiation: 1.5, boardRelationship: 1.0, managerRelationship: 0.9, transferBudgetControl: 1.0, youthFocus: 0.9 },
  },
  {
    id: 'Data Analyst',
    blurb: 'You trust the model over the hype. Cold judgment and a real feel for young talent.',
    weights: { scoutingNetwork: 1.05, judgment: 1.4, negotiation: 1.0, boardRelationship: 0.95, managerRelationship: 0.9, transferBudgetControl: 0.95, youthFocus: 1.3 },
  },
  {
    id: 'Ex-Player',
    blurb: 'You have been in the dressing room. Respect from players and managers opens doors for you.',
    weights: { scoutingNetwork: 1.1, judgment: 1.2, negotiation: 1.0, boardRelationship: 1.2, managerRelationship: 1.3, transferBudgetControl: 0.9, youthFocus: 1.1 },
  },
];

export const BASE_ATTR = 50;      // flat baseline before weighting
export const BONUS_POINTS = 20;   // pool the player spends at creation
export const MIN_ATTR = 20;
export const MAX_ATTR = 95;

const clamp = (v) => Math.min(MAX_ATTR, Math.max(MIN_ATTR, Math.round(v)));

export function backgroundById(id) {
  return DOF_BACKGROUNDS.find((b) => b.id === id) || DOF_BACKGROUNDS[0];
}

// Attributes a background starts from, before the player spends bonus points.
export function baseAttrs(bgId) {
  const bg = backgroundById(bgId);
  const out = {};
  DOF_ATTRS.forEach(({ key }) => { out[key] = clamp(BASE_ATTR * (bg.weights[key] || 1)); });
  return out;
}

// Final attributes = base + the player's bonus allocation, clamped.
export function finalAttrs(bgId, bonus = {}) {
  const base = baseAttrs(bgId);
  const out = {};
  DOF_ATTRS.forEach(({ key }) => { out[key] = clamp(base[key] + (bonus[key] || 0)); });
  return out;
}

// A single headline rating, using the AI DoF's weighting (DofMigration CA).
// The raw weights sum to 0.92, so we normalise to keep the number on a clean
// 1-100 scale comparable to the attributes themselves.
export function overallRating(attrs = {}) {
  const weighted =
    (attrs.scoutingNetwork || 0) * 0.19 +
    (attrs.negotiation || 0) * 0.15 +
    (attrs.judgment || 0) * 0.19 +
    (attrs.boardRelationship || 0) * 0.11 +
    (attrs.managerRelationship || 0) * 0.10 +
    (attrs.transferBudgetControl || 0) * 0.08 +
    (attrs.youthFocus || 0) * 0.12;
  return Math.round(weighted / 0.94);
}

// ── Bonus points: equalised so every background can reach the same ceiling ──
// Backgrounds start at slightly different overalls; the bonus pool scales so a
// player can lift any Director to TARGET_OVERALL (or stop short). ~7 attribute
// points move the overall by 1, since it averages seven attributes.
export const TARGET_OVERALL = 58;
const POINTS_PER_OVERALL = 7;

export function bonusPointsFor(bgId) {
  const baseOverall = overallRating(baseAttrs(bgId));
  return Math.max(0, Math.round(POINTS_PER_OVERALL * (TARGET_OVERALL - baseOverall)));
}

export const NATIONALITIES = [
  'English', 'Scottish', 'Welsh', 'Irish', 'Northern Irish',
  'French', 'Spanish', 'Italian', 'German', 'Dutch', 'Portuguese',
  'Brazilian', 'Argentine', 'Other',
];
